import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import MapLayerServices from "../../../services/MapLayerServices";
import Icon from "@mdi/react";
import Cookies from "js-cookie";
import {
  mdiCheckboxBlank,
  mdiCheckboxOutline,
  mdiLockOutline
} from "@mdi/js";

/**
 * A streamlined version that:
 * 1. No longer double-adds or double-removes layers on mount
 * 2. Syncs local `checked` from the store each time, so it also reacts if the heading is toggled
 * 3. handleChange dispatches to the store. Then the store triggers the effect to add/remove from the map
 */
const Layer = (props) => {
  const mapLayersService = new MapLayerServices();

  const thema = (props.data.gs_thematique || "").trim();
  const layerName = (props.data.gs_layer || "").trim();
  const layerRef = thema + ":" + layerName;

  const [locked, setLocked] = useState(false);
  // We'll let Redux be the single source of truth, but we also keep a local `checked` that we sync from Redux
  const [checked, setChecked] = useState(false);

  // If you have theming for legend
  const [bgThemeColor, setBgThemeColor] = useState("0x3B5459");
  const [themeColor, setThemeColor] = useState("0xFFFFFF");
  const [legendSrc, setLegendSrc] = useState("");

  /****************************************************************
   * 1) On mount, do minimal logic (e.g., lock logic or theme logic)
   *    We do NOT forcibly add or remove the layer here.
   ****************************************************************/
  useEffect(() => {
    // Lock logic if needed
    // if (layerName.includes("restricted") && !props.isLeader) setLocked(true);

    // Theming from cookies
    const themeCookie = Cookies.get("uetheme");
    if (themeCookie === "dark") {
      setBgThemeColor("0x3B5459");
      setThemeColor("0xFFFFFF");
    } else {
      setBgThemeColor("0xFFFFFF");
      setThemeColor("0x253437");
    }
  }, [layerName, props.isLeader]);

  /****************************************************************
   * 2) Each time the store changes, check if this layer is in it.
   *    Then we add/remove from the map (and set `checked`) accordingly.
   ****************************************************************/
  useEffect(() => {
    const inStore = props.layers_stack.includes(layerRef);

    if (inStore && !checked) {
      // It's active in Redux, but local is unchecked => add to the map, set checked
      addLayerToMap(layerRef);
      setChecked(true);
    } else if (!inStore && checked) {
      // It's not in Redux, but local is checked => remove from the map, set unchecked
      removeLayerFromMap(layerRef);
      setChecked(false);
    }
    // If inStore && checked => do nothing (already synced)
    // If !inStore && !checked => do nothing (already off)
  }, [props.layers_stack, layerRef, checked]);

  /****************************************************************
   * 3) Build the legend URL each time the theme changes or data changes
   ****************************************************************/
  useEffect(() => {
    if (!props.geoserverApiUrl) return;

    const newLegendUrl = 
      `${props.geoserverApiUrl}` +
      `?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=50&HEIGHT=25` +
      `&LAYER=${thema}:${layerName}` +
      `&legend_options=fontName:DejaVu%20San%20Condensed;fontAntiAliasing:true;` +
      `fontColor:${themeColor};bgColor:${bgThemeColor};fontSize:9;forceLabels:on;wrap:false;dpi:90`;
    setLegendSrc(newLegendUrl);
  }, [bgThemeColor, themeColor, props.data, props.geoserverApiUrl]);

  /****************************************************************
   * 4) When user directly toggles this layer
   *    we just update Redux. The `useEffect` above handles the map part.
   ****************************************************************/
  const handleChange = () => {
    if (locked) return;
    const inStore = props.layers_stack.includes(layerRef);

    if (inStore) {
      // The layer is in store => remove from store
      props.removeLayerFromStack(layerRef);
    } else {
      // The layer is not in store => add it
      if (props.data.type === 0) {
        props.addLayerToStack(layerRef);
      }
    }
  };

  /****************************************************************
   * 5) Helpers for adding/removing from the map
   ****************************************************************/
  const addLayerToMap = (ref) => {
    const mapRef = $("#map").data("map"); // jQuery approach
    if (mapRef) {
      mapLayersService.addLayer(mapRef, [ref]);
    }
  };

  const removeLayerFromMap = (ref) => {
    const mapRef = $("#map").data("map");
    if (mapRef) {
      mapLayersService.removeOneLayer(ref);
    }
  };

  return (
    <div>
      <div className="p-0 pl-3">
        <form>
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {/* The clickable icon */}
            <div
              style={{ cursor: locked ? "default" : "pointer" }}
              onClick={handleChange}
            >
              {locked ? (
                <Icon path={mdiLockOutline} size={0.8} color="#D9D9D9FF" />
              ) : checked ? (
                <Icon
                  path={mdiCheckboxOutline}
                  size={0.75}
                  color="var(--theme-primary)"
                />
              ) : (
                <Icon
                  path={mdiCheckboxBlank}
                  size={0.7}
                  color="var(--theme-dark)"
                />
              )}
            </div>

            {/* The legend image */}
            <div
              style={{
                marginLeft: "5px",
                cursor: locked ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
              }}
              onClick={handleChange}
            >
              {legendSrc && (
                <img
                  alt={`leg_${layerRef}`}
                  src={legendSrc}
                  style={{
                    border: "0px",
                    outline: "none",
                    marginRight: "3px",
                  }}
                />
              )}
              {/* If you want the textual label here:
              <span style={{ whiteSpace: "normal" }}>
                {props.data.title || layerRef}
              </span>*/}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/****************************************************************
 * Redux
 ****************************************************************/
const mapStateToProps = (state) => ({
  layers_stack: state.LayersReducer.layers_stack,
  geoserverApiUrl: state.AppReducer.geoserverApiUrl,
  isLeader: state.MeReducer.isLeader,
});

const mapDispatchToProps = (dispatch) => ({
  addLayerToStack: (ref) => dispatch({ type: "ADD_LAYER_TO_STACK", data: ref }),
  removeLayerFromStack: (ref) =>
    dispatch({ type: "REMOVE_LAYER_FROM_STACK", data: ref }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Layer);
